// client/src/coms/voip/voiceclient_parts/audioRuntime.js
import { Track, createLocalAudioTrack } from 'livekit-client';
import { getPreferredInputDeviceId, getPreferredOutputDeviceId } from '../../audio/audioDevicePrefs';
import { getNoiseGateThresholdDb } from '../noiseGatePrefs';
import { getPushToTalkEnabled } from '../pushToTalkPrefs';
import { audioElsByTrackSid, audioMetaByTrackSid, VOICE_AUDIO_BITRATE_BPS, VOICE_AUDIO_RED, voiceState } from './state';
import { canSetSinkId, isTauriRuntime } from './utils';
import { ensureGateCtx, primeGateCtx } from './sfx';
import {
  broadcastLocalSpeakingState,
  broadcastLocalVoiceAnimation,
  broadcastLocalVoiceStatus,
  updateLocalSpeakingNow,
} from './packets';
import { applyRemoteAudioAudibility, applyRemoteAudioElementPrefs } from './proximity';
import { isVoiceAudioPublication } from '../screenShareAudioManager';
import { createProcessedMediaStreamTrackFromTrack } from '../audioEffects';
import { refreshSoundboardOutput } from '../../soundboard/soundboardEngine';

function getEffectiveVoiceOutputDeviceId() {
  return String(getPreferredOutputDeviceId() || '').trim();
}

async function applyPreferredOutputToEl(el) {
  if (!el || !canSetSinkId()) return;

  const outputId = getEffectiveVoiceOutputDeviceId();

  try {
    await el.setSinkId(outputId || 'default');
  } catch {}
}

export async function applyPreferredOutputToAllEls() {
  try {
    refreshSoundboardOutput(); // soundboard audio follows the voice output device
  } catch {}
  if (!canSetSinkId()) return;

  for (const [, el] of audioElsByTrackSid.entries()) {
    await applyPreferredOutputToEl(el);
  }
}

function getSpeechZoneSignature(zone) {
  if (!zone) return '';
  const shapeId = zone.shapeId ?? zone.id ?? null;
  const behaviorType = zone.behaviorType ?? zone.behavior_type ?? null;
  const behaviorProps = zone.behaviorProps ?? zone.behavior_props ?? null;
  try {
    return JSON.stringify({
      shapeId,
      behaviorType,
      behaviorProps,
    });
  } catch {
    return `${String(shapeId || '')}:${String(behaviorType || '')}`;
  }
}

function getSpeechZonePayload(zone) {
  if (!zone) return null;
  const behaviorType = String(zone.behaviorType ?? zone.behavior_type ?? '').trim().toLowerCase();
  if (behaviorType !== 'speech_effects') return null;

  const propsRaw = zone.behaviorProps ?? zone.behavior_props ?? {};
  const props = propsRaw && typeof propsRaw === 'object' && !Array.isArray(propsRaw) ? propsRaw : {};

  const reverbAmountRaw = Number(props.reverbAmount);
  const reverbAmount = Number.isFinite(reverbAmountRaw) ? Math.max(0, Math.min(10, reverbAmountRaw)) : 1;

  const reverbCharacter = String(props.reverbCharacter || 'medium').trim().toLowerCase() || 'medium';

  const compressionAmountRaw = Number(props.compressionAmount);
  const compressionAmount = Number.isFinite(compressionAmountRaw) ? Math.max(0, Math.min(10, compressionAmountRaw)) : 0;

  const brightnessAmountRaw = Number(props.brightnessAmount);
  const brightnessAmount = Number.isFinite(brightnessAmountRaw) ? Math.max(0, Math.min(10, brightnessAmountRaw)) : 5;

  return {
    shapeId: zone.shapeId ?? zone.id ?? null,
    behaviorType: 'speech_effects',
    reverbAmount,
    reverbCharacter,
    compressionAmount,
    brightnessAmount,
    behaviorProps: props,
  };
}

function destroySpeechEffectsPipeline() {
  const fx = voiceState.speechEffectsPipeline;
  voiceState.speechEffectsPipeline = null;
  voiceState.activeSpeechEffectSignature = '';
  voiceState.activeSpeechEffectApplied = null;

  if (!fx) return;

  try {
    if (typeof fx.destroy === 'function') fx.destroy();
  } catch {}
}

function applySpeechZoneToPipeline(zone) {
  voiceState.localSpeechZone = zone || null;

  const fx = voiceState.speechEffectsPipeline;
  if (!fx) return;

  const payload = getSpeechZonePayload(zone);
  const signature = getSpeechZoneSignature(zone);

  if (!payload) {
    try {
      fx.clearPreset({ smoothing: 0.2 });
    } catch {}
    voiceState.activeSpeechEffectSignature = '';
    voiceState.activeSpeechEffectApplied = null;
    return;
  }

  if (voiceState.activeSpeechEffectSignature === signature) {
    return;
  }

  try {
    fx.applyPreset('dry', {
      smoothing: 0.2,
      reverbAmountOverride: payload.reverbAmount,
      reverbCharacterOverride: payload.reverbCharacter,
      compressionAmountOverride: payload.compressionAmount,
      brightnessAmountOverride: payload.brightnessAmount,
    });
    voiceState.activeSpeechEffectSignature = signature;
    voiceState.activeSpeechEffectApplied = payload;
  } catch {}

  try {
    if (typeof fx.resume === 'function') fx.resume();
  } catch {}
}

export function setLocalSpeechEffectsZone(zone) {
  applySpeechZoneToPipeline(zone || null);
}

export function clearLocalSpeechEffectsZone() {
  applySpeechZoneToPipeline(null);
}

export function bindSpeechEffectsOnce() {
  if (voiceState.speechEffectsBound) return;
  voiceState.speechEffectsBound = true;

  window.addEventListener(
    'pa-local-speech-zone',
    (e) => {
      const zone = e && e.detail ? e.detail.zone ?? null : null;
      try {
        setLocalSpeechEffectsZone(zone);
      } catch {}
    },
    { passive: true }
  );
}

export function attachRemoteAudioTrack(track, participant, pub = null) {
  if (!track) return;
  if (!track.mediaStreamTrack) return;
  if (track.kind !== 'audio') return;
  if (pub && !isVoiceAudioPublication(participant, pub)) return;

  const sid = String(track.sid || '');
  if (!sid) return;

  if (audioElsByTrackSid.has(sid)) return;

  const el = document.createElement('audio');
  el.autoplay = true;
  el.playsInline = true;
  el.muted = false;
  el.volume = voiceState.manualDeafened ? 0 : 1;

  try {
    el.srcObject = new MediaStream([track.mediaStreamTrack]);
  } catch {
    return;
  }

  const participantIdentity = String((participant && participant.identity) || '').trim();
  const participantName = String((participant && participant.name) || '').trim();

  audioElsByTrackSid.set(sid, el);
  audioMetaByTrackSid.set(sid, {
    sid,
    participantIdentity,
    participantName,
  });

  try {
    document.body.appendChild(el);
  } catch {}

  try {
    applyPreferredOutputToEl(el).catch(() => {});
  } catch {}

  try {
    el.play().catch(() => {});
  } catch {}

  try {
    applyRemoteAudioElementPrefs(voiceState.activeRoom, {});
  } catch {}
}

export function detachRemoteTrack(track) {
  if (!track) return;
  const sid = String(track.sid || '');
  if (!sid) return;

  const el = audioElsByTrackSid.get(sid);
  if (!el) return;

  try {
    el.srcObject = null;
  } catch {}
  try {
    el.remove();
  } catch {}

  audioElsByTrackSid.delete(sid);
  audioMetaByTrackSid.delete(sid);
}

export function detachAllRemoteAudio() {
  for (const [, el] of audioElsByTrackSid.entries()) {
    try {
      el.srcObject = null;
    } catch {}
    try {
      el.remove();
    } catch {}
  }
  audioElsByTrackSid.clear();
  audioMetaByTrackSid.clear();
}

function setLocalOutputGain(g) {
  const gg = voiceState.gateGain;
  if (!gg) return;

  const v = Number(g);
  const clamped = !Number.isFinite(v) ? 1 : v < 0 ? 0 : v > 1 ? 1 : v;

  try {
    const ctx = voiceState.gateCtx;
    const now = ctx ? ctx.currentTime : 0;
    gg.gain.cancelScheduledValues(now);
    gg.gain.setTargetAtTime(clamped, now, 0.015);
  } catch {
    try {
      gg.gain.value = clamped;
    } catch {}
  }
}

function shouldTransmitFromPtt() {
  const enabled = !!getPushToTalkEnabled();
  if (!enabled) return true;
  if (!isTauriRuntime()) return true;
  return !!voiceState.pttHeld;
}

function recomputeLocalTransmit() {
  const pttOk = shouldTransmitFromPtt();
  const gateOk = !!voiceState.gateOpen;
  const micOk = !voiceState.manualMicMuted;
  const on = pttOk && gateOk && micOk;
  setLocalOutputGain(on ? 1 : 0);
}

function applyPushToTalkStateToLocalTrack() {
  recomputeLocalTransmit();
  broadcastLocalSpeakingState(true);
}

export function stopGatePipeline() {
  if (voiceState.gateTimer) {
    try {
      clearInterval(voiceState.gateTimer);
    } catch {}
    voiceState.gateTimer = null;
  }

  try {
    if (voiceState.gateSource) voiceState.gateSource.disconnect();
  } catch {}
  try {
    if (voiceState.gateAnalyser) voiceState.gateAnalyser.disconnect();
  } catch {}
  try {
    if (voiceState.gateGain) voiceState.gateGain.disconnect();
  } catch {}
  try {
    if (voiceState.gateKeepAlive) voiceState.gateKeepAlive.disconnect();
  } catch {}

  voiceState.gateSource = null;
  voiceState.gateAnalyser = null;
  voiceState.gateGain = null;
  voiceState.gateDest = null;
  voiceState.gateKeepAlive = null;

  destroySpeechEffectsPipeline();

  voiceState.gateOpen = true;
  voiceState.gateLastAboveAt = 0;
  voiceState.gateLastApplyAt = 0;
  updateLocalSpeakingNow(false, 0, -100);
}

export function startGatePipelineFromCaptureTrack(captureTrack) {
  stopGatePipeline();

  const mst = captureTrack && captureTrack.mediaStreamTrack ? captureTrack.mediaStreamTrack : null;
  if (!mst) {
    voiceState.gateOpen = true;
    updateLocalSpeakingNow(false, 0, -100);
    recomputeLocalTransmit();
    return { processedTrack: mst, cleanup: () => {} };
  }

  const ctx = ensureGateCtx();
  if (!ctx) {
    voiceState.gateOpen = true;
    updateLocalSpeakingNow(false, 0, -100);
    recomputeLocalTransmit();
    return { processedTrack: mst, cleanup: () => {} };
  }

  primeGateCtx();

  let processed = mst;

  try {
    const stream = new MediaStream([mst]);

    const source = ctx.createMediaStreamSource(stream);

    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.2;

    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -28;
    compressor.knee.value = 24;
    compressor.ratio.value = 6;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.25;

    const makeup = ctx.createGain();
    makeup.gain.value = 1.35;

    const g = ctx.createGain();
    g.gain.value = 1;

    const dest = ctx.createMediaStreamDestination();

    source.connect(compressor);
    compressor.connect(makeup);
    makeup.connect(g);
    g.connect(dest);

    source.connect(analyser);

    const keepAlive = ctx.createGain();
    keepAlive.gain.value = 0;
    makeup.connect(keepAlive);
    analyser.connect(keepAlive);
    keepAlive.connect(ctx.destination);

    voiceState.gateSource = source;
    voiceState.gateAnalyser = analyser;
    voiceState.gateGain = g;
    voiceState.gateDest = dest;
    voiceState.gateKeepAlive = keepAlive;

    processed = dest.stream.getAudioTracks && dest.stream.getAudioTracks()[0] || mst;
  } catch {
    voiceState.gateOpen = true;
    updateLocalSpeakingNow(false, 0, -100);
    recomputeLocalTransmit();
    return { processedTrack: mst, cleanup: () => {} };
  }

  let speechFx = null;

  try {
    speechFx = createProcessedMediaStreamTrackFromTrack(processed, {
      ctx,
      preset: 'dry',
    });
  } catch {
    speechFx = null;
  }

  if (speechFx && speechFx.track) {
    voiceState.speechEffectsPipeline = speechFx;
    processed = speechFx.track;
    try {
      speechFx.resume();
    } catch {}
    applySpeechZoneToPipeline(voiceState.localSpeechZone || null);
  } else {
    destroySpeechEffectsPipeline();
  }

  const buf = new Float32Array(voiceState.gateAnalyser.fftSize);

  const OPEN_HOLD_MS = 900;
  const MIN_APPLY_INTERVAL_MS = 40;

  voiceState.gateOpen = true;
  voiceState.gateLastAboveAt = Date.now();
  voiceState.gateLastApplyAt = 0;
  updateLocalSpeakingNow(false, 0, -100);

  // Open the gate the moment the page is hidden (the throttled timer could take seconds to notice).
  if (typeof document !== 'undefined' && !voiceState.gateVisibilityBound) {
    voiceState.gateVisibilityBound = true;
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden || !voiceState.gateAnalyser || voiceState.gateOpen) return;
      voiceState.gateOpen = true;
      voiceState.gateLastAboveAt = Date.now();
      try {
        recomputeLocalTransmit();
      } catch {}
    });
  }

  voiceState.gateTimer = setInterval(() => {
    try {
      if (voiceState.gateCtx && voiceState.gateCtx.state === 'suspended') voiceState.gateCtx.resume();
    } catch {}

    if (!voiceState.gateAnalyser) return;

    // Browsers throttle timers in hidden pages (background tab, minimised or covered window) to about
    // once a second, later once a minute, so this gate would keep the mic closed while the user talks.
    // While hidden, transmit without gating (manual mute and push-to-talk still apply).
    if (typeof document !== 'undefined' && document.hidden) {
      if (!voiceState.gateOpen) {
        voiceState.gateOpen = true;
        voiceState.gateLastAboveAt = Date.now();
        recomputeLocalTransmit();
      }
      return;
    }

    try {
      voiceState.gateAnalyser.getFloatTimeDomainData(buf);
    } catch {
      return;
    }

    let peak = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = buf[i];
      const av = v < 0 ? -v : v;
      if (av > peak) peak = av;
    }

    const dbPeak = peak > 0 ? 20 * Math.log10(peak) : -100;
    const db = Math.max(-100, Math.min(0, dbPeak));

    const thresholdDb = Number(getNoiseGateThresholdDb());
    const now = Date.now();

    const pttOk = shouldTransmitFromPtt();
    const micOk = !voiceState.manualMicMuted;
    const aboveThreshold = db >= thresholdDb;
    const speakingNow = !!(pttOk && micOk && aboveThreshold);

    updateLocalSpeakingNow(speakingNow, peak, db);

    if (aboveThreshold) {
      voiceState.gateLastAboveAt = now;
      if (!voiceState.gateOpen) {
        voiceState.gateOpen = true;
        if (now - voiceState.gateLastApplyAt >= MIN_APPLY_INTERVAL_MS) {
          voiceState.gateLastApplyAt = now;
          recomputeLocalTransmit();
        }
      }
      return;
    }

    if (now - voiceState.gateLastAboveAt < OPEN_HOLD_MS) {
      if (!voiceState.gateOpen) {
        voiceState.gateOpen = true;
        if (now - voiceState.gateLastApplyAt >= MIN_APPLY_INTERVAL_MS) {
          voiceState.gateLastApplyAt = now;
          recomputeLocalTransmit();
        }
      }
      return;
    }

    if (voiceState.gateOpen) {
      voiceState.gateOpen = false;
      if (now - voiceState.gateLastApplyAt >= MIN_APPLY_INTERVAL_MS) {
        voiceState.gateLastApplyAt = now;
        recomputeLocalTransmit();
      }
    }
  }, 33);

  return {
    processedTrack: processed,
    cleanup: () => {
      stopGatePipeline();
    },
  };
}

export function bindNoiseGateOnce() {
  if (voiceState.gateBound) return;
  voiceState.gateBound = true;

  window.addEventListener(
    'coms-noise-gate-changed',
    () => {
      try {
        recomputeLocalTransmit();
        broadcastLocalSpeakingState(true);
      } catch {}
    },
    { passive: true }
  );
}

function findPublishedLocalAudioPublication(room, track) {
  const r = room || voiceState.activeRoom;
  const t = track || voiceState.activeProcessedTrack;
  if (!r || !r.localParticipant || !t) return null;

  try {
    const pubs = Array.from(r.localParticipant.audioTrackPublications?.values() || []);
    for (const pub of pubs) {
      if (!pub) continue;
      if (pub.track === t) return pub;
      if (pub.track && t && pub.track.sid && t.sid && pub.track.sid === t.sid) return pub;
      if (pub.track && t && pub.track.mediaStreamTrack === t) return pub;
    }
  } catch {}

  return null;
}

function muteAllRemoteAudioForMeter() {
  try {
    for (const [, el] of audioElsByTrackSid.entries()) {
      if (!el) continue;
      try {
        el.dataset.paMeterWasMuted = el.muted ? '1' : '0';
      } catch {}
      try {
        el.muted = true;
      } catch {}
      try {
        el.volume = 0;
      } catch {}
    }
  } catch {}
}

function unmuteAllRemoteAudioAfterMeter() {
  try {
    for (const [, el] of audioElsByTrackSid.entries()) {
      if (!el) continue;
      let shouldStayMuted = false;
      try {
        shouldStayMuted = el.dataset.paMeterWasMuted === '1';
      } catch {}
      try {
        delete el.dataset.paMeterWasMuted;
      } catch {}
      try {
        el.muted = shouldStayMuted || !!voiceState.manualDeafened;
      } catch {}
      try {
        if (!shouldStayMuted) el.volume = voiceState.manualDeafened ? 0 : 1;
      } catch {}
    }
  } catch {}

  try {
    applyRemoteAudioElementPrefs(voiceState.activeRoom, {});
  } catch {}
}

function suppressNetworkMicForMeter(room) {
  const pub = findPublishedLocalAudioPublication(room, voiceState.activeProcessedTrack);
  if (!pub) return;

  try {
    if (typeof pub.setMuted === 'function') pub.setMuted(true);
  } catch {}
}

function restoreNetworkMicAfterMeter(room) {
  const pub = findPublishedLocalAudioPublication(room, voiceState.activeProcessedTrack);
  if (!pub) return;

  try {
    if (typeof pub.setMuted === 'function') pub.setMuted(!!voiceState.manualMicMuted);
  } catch {}
}

function startLocalMonitorForMeter() {
  if (voiceState.meterMonitorEl) return;
  if (!voiceState.activeCaptureTrack || !voiceState.activeCaptureTrack.mediaStreamTrack) return;

  const stream = new MediaStream([voiceState.activeCaptureTrack.mediaStreamTrack]);
  const el = document.createElement('audio');
  el.autoplay = true;
  el.playsInline = true;
  el.muted = false;
  el.volume = 1;

  try {
    el.srcObject = stream;
  } catch {
    return;
  }

  voiceState.meterMonitorEl = el;
  voiceState.meterMonitorStream = stream;

  try {
    document.body.appendChild(el);
  } catch {}

  try {
    applyPreferredOutputToEl(el).catch(() => {});
  } catch {}

  try {
    el.play().catch(() => {});
  } catch {}
}

function stopLocalMonitorForMeter() {
  if (!voiceState.meterMonitorEl) return;

  try {
    voiceState.meterMonitorEl.pause();
  } catch {}
  try {
    voiceState.meterMonitorEl.srcObject = null;
  } catch {}
  try {
    voiceState.meterMonitorEl.remove();
  } catch {}

  voiceState.meterMonitorEl = null;
  voiceState.meterMonitorStream = null;
}

export function setMeterMonitorMode(on) {
  const enabled = !!on;

  if (voiceState.meterMonitorOn === enabled) return;
  voiceState.meterMonitorOn = enabled;

  if (enabled) {
    try {
      muteAllRemoteAudioForMeter();
    } catch {}

    try {
      startLocalMonitorForMeter();
    } catch {}

    try {
      suppressNetworkMicForMeter(voiceState.activeRoom);
    } catch {}

    return;
  }

  try {
    stopLocalMonitorForMeter();
  } catch {}

  try {
    unmuteAllRemoteAudioAfterMeter();
  } catch {}

  try {
    restoreNetworkMicAfterMeter(voiceState.activeRoom);
  } catch {}
}

export function bindMeterMonitorOnce() {
  if (voiceState.meterMonitorBound) return;
  voiceState.meterMonitorBound = true;

  window.addEventListener(
    'coms-mic-meter-toggled',
    (e) => {
      const running = !!(e && e.detail && e.detail.running);
      setMeterMonitorMode(running);
    },
    { passive: true }
  );
}

export async function createPreferredLocalAudioTrack() {
  const inputId = String(getPreferredInputDeviceId() || '').trim();

  if (!inputId) {
    return await createLocalAudioTrack({
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    });
  }

  try {
    return await createLocalAudioTrack({
      deviceId: inputId,
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    });
  } catch {
    return await createLocalAudioTrack({
      constraints: {
        deviceId: { exact: inputId },
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
  }
}

export async function switchMicToPreferredDevice() {
  if (!voiceState.activeRoom) return;
  if (!voiceState.activeRoom.localParticipant) return;
  if (voiceState.switchingMic) return;

  voiceState.switchingMic = true;
  try {
    const newCapture = await createPreferredLocalAudioTrack();

    try {
      if (voiceState.activeProcessedTrack) {
        try {
          const pub = findPublishedLocalAudioPublication(voiceState.activeRoom, voiceState.activeProcessedTrack);
          if (pub && pub.track) voiceState.activeRoom.localParticipant.unpublishTrack(pub.track, false);
          else voiceState.activeRoom.localParticipant.unpublishTrack(voiceState.activeProcessedTrack, false);
        } catch {}
      }
    } catch {}

    try {
      if (voiceState.activeCaptureTrack) {
        try {
          voiceState.activeCaptureTrack.stop();
        } catch {}
      }
    } catch {}

    stopGatePipeline();

    const pipe = startGatePipelineFromCaptureTrack(newCapture);
    const processedMst = pipe.processedTrack;

    voiceState.activeCaptureTrack = newCapture;
    voiceState.activeProcessedTrack = processedMst;

    if (voiceState.meterMonitorOn) {
      voiceState.activeLocalPub = null;
      stopLocalMonitorForMeter();
      startLocalMonitorForMeter();
      suppressNetworkMicForMeter(voiceState.activeRoom);
      voiceState.pttHeld = false;
      recomputeLocalTransmit();
      broadcastLocalSpeakingState(true);
      broadcastLocalVoiceStatus(true);
      broadcastLocalVoiceAnimation(true);
      return;
    }

    let pub2 = null;
    try {
      pub2 = await voiceState.activeRoom.localParticipant.publishTrack(processedMst, {
        source: Track.Source.Microphone,
        audioBitrate: VOICE_AUDIO_BITRATE_BPS,
        dtx: true,
        red: VOICE_AUDIO_RED,
        forceStereo: false,
      });
    } catch {
      pub2 = null;
      await voiceState.activeRoom.localParticipant.publishTrack(processedMst, {
        source: Track.Source.Microphone,
        audioBitrate: VOICE_AUDIO_BITRATE_BPS,
        dtx: true,
        red: VOICE_AUDIO_RED,
        forceStereo: false,
      });
    }

    voiceState.activeLocalPub = pub2;

    voiceState.pttHeld = false;
    recomputeLocalTransmit();
    broadcastLocalSpeakingState(true);
    broadcastLocalVoiceStatus(true);
    broadcastLocalVoiceAnimation(true);
  } catch {
    return;
  } finally {
    voiceState.switchingMic = false;
  }
}

export function bindDeviceChangeListenerOnce() {
  if (voiceState.boundDeviceListener) return;
  voiceState.boundDeviceListener = true;

  window.addEventListener(
    'coms-audio-device-changed',
    async () => {
      try {
        await applyPreferredOutputToAllEls();
      } catch {}

      try {
        await switchMicToPreferredDevice();
      } catch {}
    },
    { passive: true }
  );
}

export function bindPushToTalkOnce() {
  if (voiceState.pttBound) return;
  voiceState.pttBound = true;

  const applyPttDownState = (nextDown) => {
    try {
      if (!getPushToTalkEnabled()) {
        if (voiceState.pttHeld) {
          voiceState.pttHeld = false;
          applyPushToTalkStateToLocalTrack();
        }
        return;
      }

      if (!isTauriRuntime()) {
        if (voiceState.pttHeld) {
          voiceState.pttHeld = false;
          applyPushToTalkStateToLocalTrack();
        }
        return;
      }

      const down = !!nextDown;
      if (voiceState.pttHeld === down) return;

      voiceState.pttHeld = down;
      applyPushToTalkStateToLocalTrack();
    } catch {}
  };

  const applyTauriPttState = payload => {
    try {
      applyPttDownState(!!(payload && payload.down));
    } catch {}
  };

  const applyLegacyInjectedPttState = eventLike => {
    try {
      const detail = eventLike && eventLike.detail && typeof eventLike.detail === 'object' ? eventLike.detail : {};
      if (typeof detail.down === 'boolean') {
        applyPttDownState(detail.down);
        return;
      }

      const state = String(detail.state || '').trim().toLowerCase();
      if (state === 'pressed' || state === 'down' || state === 'keydown') {
        applyPttDownState(true);
        return;
      }
      if (state === 'released' || state === 'up' || state === 'keyup') {
        applyPttDownState(false);
      }
    } catch {}
  };

  const bindLegacyInjectedListeners = () => {
    if (!voiceState.pttWindowEventBound) {
      voiceState.pttWindowEventBound = true;
      try {
        window.addEventListener('coms-ptt-global-hotkey', applyLegacyInjectedPttState, { passive: true });
      } catch {}
    }

    if (!voiceState.pttDocumentEventBound) {
      voiceState.pttDocumentEventBound = true;
      try {
        document.addEventListener('coms-ptt-global-hotkey', applyLegacyInjectedPttState, { passive: true });
      } catch {}
    }
  };

  const bindTauriPttListener = async () => {
    try {
      if (!isTauriRuntime()) return;
      if (voiceState.pttTauriUnlisten) return;

      let tauriListen = null;

      try {
        if (window.__TAURI__ && window.__TAURI__.event && typeof window.__TAURI__.event.listen === 'function') {
          tauriListen = window.__TAURI__.event.listen;
        }
      } catch {}

      if (!tauriListen) {
        try {
          const mod = await import('@tauri-apps/api/event');
          if (mod && typeof mod.listen === 'function') {
            tauriListen = mod.listen;
          }
        } catch {}
      }

      if (!tauriListen) return;

      const unlisten = await tauriListen('ptt_state', event => {
        try {
          applyTauriPttState(event && event.payload ? event.payload : null);
        } catch {}
      });

      if (typeof unlisten === 'function') {
        voiceState.pttTauriUnlisten = unlisten;
      }
    } catch {}
  };

  window.addEventListener(
    'coms-ptt-changed',
    () => {
      try {
        voiceState.pttHeld = false;
        applyPushToTalkStateToLocalTrack();
      } catch {}
    },
    { passive: true }
  );

  bindLegacyInjectedListeners();
  bindTauriPttListener();

  if (!getPushToTalkEnabled() || !isTauriRuntime()) {
    voiceState.pttHeld = false;
    applyPushToTalkStateToLocalTrack();
  }
}

export function bindParticipantSpeakingEvents(participant, notify) {
  const p = participant || null;
  if (!p || typeof p.on !== 'function') return;

  const fire = () => {
    try {
      notify();
    } catch {}
  };

  try {
    p.on('isSpeakingChanged', fire);
  } catch {}

  try {
    p.on('trackMuted', fire);
  } catch {}

  try {
    p.on('trackUnmuted', fire);
  } catch {}

  try {
    p.on('trackPublished', fire);
  } catch {}

  try {
    p.on('trackUnpublished', fire);
  } catch {}
}

export function getLocalMicMuted() {
  return !!voiceState.manualMicMuted;
}

export function getLocalDeafened() {
  return !!voiceState.manualDeafened;
}

export function setLocalMicMuted(next) {
  voiceState.manualMicMuted = !!next;

  try {
    const pub = findPublishedLocalAudioPublication(voiceState.activeRoom, voiceState.activeProcessedTrack);
    if (pub && typeof pub.setMuted === 'function') {
      pub.setMuted(voiceState.manualMicMuted);
    }
  } catch {}

  try {
    if (voiceState.manualMicMuted) updateLocalSpeakingNow(false, 0, voiceState.localSpeakingDb);
    recomputeLocalTransmit();
    broadcastLocalSpeakingState(true);
    broadcastLocalVoiceStatus(true);
  } catch {}

  return voiceState.manualMicMuted;
}

export function toggleLocalMicMuted() {
  return setLocalMicMuted(!voiceState.manualMicMuted);
}

export function setLocalDeafened(next) {
  voiceState.manualDeafened = !!next;

  try {
    refreshSoundboardOutput(); // deafened hears no soundboard sounds either
  } catch {}

  try {
    if (!voiceState.meterMonitorOn) applyRemoteAudioAudibility();
    broadcastLocalVoiceStatus(true);
  } catch {}

  return voiceState.manualDeafened;
}

export function toggleLocalDeafened() {
  return setLocalDeafened(!voiceState.manualDeafened);
}